import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import { SystemRole } from '@/types/roles';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: SystemRole;
  allowedRoles?: SystemRole[];
  requireAuth?: boolean;
}

const ProtectedRoute = ({ 
  children, 
  requiredRole, 
  allowedRoles,
  requireAuth = true 
}: ProtectedRouteProps) => {
  const { user, profile, loading, isEffectiveVendor } = useAuth();
  const hasRelayManagerAccess = profile?.has_vendor_management_access === true;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/auth" replace />;
  }

  // CORRECTION TEMPORAIRE: Traiter tcyteam@gmail.com comme super_admin si system_role est undefined
  let effectiveRole = profile?.system_role;
  if (!effectiveRole && profile?.email === 'tcyteam@gmail.com') {
    console.log('🔧 PROTECTEDROUTE: Correction temporaire - tcyteam@gmail.com traité comme super_admin');
    effectiveRole = 'super_admin';
  }
  
  // Check if user has required role or is in allowed roles
  let hasAccess = true;
  if (allowedRoles) {
    const vendorAllowed = allowedRoles.includes('vendor');
    const relayAllowed = allowedRoles.includes('relay');
    // Allow access to vendor tools if user has vendor role, regardless of approval status
    hasAccess =
      allowedRoles.includes(effectiveRole as any) ||
      (vendorAllowed && effectiveRole === 'vendor') ||
      (relayAllowed && hasRelayManagerAccess);
  } else if (requiredRole) {
    if (requiredRole === 'vendor') {
      // Allow access to vendor tools if user has vendor role, regardless of approval status
      hasAccess = effectiveRole === 'vendor' || isEffectiveVendor;
    } else if (requiredRole === 'relay') {
      hasAccess = effectiveRole === 'relay' || hasRelayManagerAccess;
    } else {
      hasAccess = effectiveRole === requiredRole;
    }
  }

  if ((requiredRole || allowedRoles) && !hasAccess) {
    // Redirect based on user's actual role (using effectiveRole for corrections)
    switch (effectiveRole) {
      case 'admin':
      case 'hr':
      case 'super_admin':
        return <Navigate to="/admin" replace />;
      case 'vendor':
        return <Navigate to="/vendor" replace />;
      case 'relay':
        return <Navigate to="/relay" replace />;
      case 'client':
      default:
        return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;